import { useState, useMemo, useEffect } from "react";
import type { Field, Group, GroupPayload } from "../types";
import { StringField } from "../fields/StringField";
import { IntField } from "../fields/IntField";
import { BoolField } from "../fields/BoolField";
import { SecretField } from "../fields/SecretField";

type FormPayload = Extract<GroupPayload, { kind: "form" }>;

interface Props {
  group: Group;
  payload: FormPayload;
  onSave: (body: Record<string, unknown>) => void | Promise<void>;
  onTest?: () => void;
  saving?: boolean;
  error?: string | null;
  onDirtyChange?: (dirty: boolean) => void;
}

type DraftValue = { kind: "set"; value: unknown } | { kind: "delete" };

// coerceBoolFieldValue converts the server's string representation of a bool
// field into an actual boolean. The Go side stores every setting as a string
// ("true" / "false" / "1" / "0") because the settings table is one TEXT column,
// and loadFormPayload echoes the stored string back verbatim. A naive
// Boolean(value) call in the renderer treats the string "false" as truthy
// (non-empty strings are always truthy in JS), so the toggle stayed ON even
// after the user had saved OFF. Mirror saas-common/settings.ResolveBool's
// accepted value set so a value stored by any path round-trips correctly.
function coerceBoolFieldValue(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes" || s === "y" || s === "on";
  }
  return false;
}

function evalHideWhen(
  expr: string | undefined,
  draft: Record<string, DraftValue>,
  payload: FormPayload,
): boolean {
  if (!expr) return false;
  const m = expr.match(/^(\w+)=(.+)$/);
  if (!m) return false;
  const [, key, wanted] = m;
  const d = draft[key];
  if (d?.kind === "set") return String(d.value) === wanted;
  const fv = payload.fields[key];
  if (fv && "value" in fv) return String(fv.value) === wanted;
  return false;
}

export function FormPanel({
  group,
  payload,
  onSave,
  onTest,
  saving,
  error,
  onDirtyChange,
}: Props) {
  const [draft, setDraft] = useState<Record<string, DraftValue>>({});
  const dirty = useMemo(() => Object.keys(draft).length > 0, [draft]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  const fields = group.fields ?? [];

  function readValue(f: Field): unknown {
    const d = draft[f.key];
    if (d?.kind === "set") return d.value;
    const fv = payload.fields[f.key];
    if (!fv) return "";
    if ("value" in fv) return fv.value ?? "";
    return "";
  }

  function setField(f: Field, value: unknown) {
    // null / undefined explicitly mean "clear this field" — translate to a
    // delete-action in the PUT payload so the env-var fallback can resurface
    // as a placeholder. Storing 0/"" instead would shadow any env value.
    if (value === null || value === undefined) {
      setDraft((s) => ({ ...s, [f.key]: { kind: "delete" } }));
      return;
    }
    setDraft((s) => ({ ...s, [f.key]: { kind: "set", value } }));
  }

  function unsetField(f: Field) {
    setDraft((s) => {
      const n = { ...s };
      delete n[f.key];
      return n;
    });
  }

  function buildPayload(): Record<string, unknown> {
    const hiddenKeys = new Set<string>();
    for (const f of fields) {
      if (evalHideWhen(f.hide_when, draft, payload)) hiddenKeys.add(f.key);
    }
    const out: Record<string, unknown> = {};
    for (const [k, d] of Object.entries(draft)) {
      if (hiddenKeys.has(k)) continue;
      if (d.kind === "delete") out[k] = null;
      if (d.kind === "set") out[k] = d.value;
    }
    return out;
  }

  function reset() {
    setDraft({});
  }

  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <header className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-card-foreground">
            {group.title}
            {dirty && (
              <span className="ml-2 align-middle text-xs font-normal text-warning">
                ● unsaved
              </span>
            )}
          </h2>
          {group.description && (
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {group.description}
            </p>
          )}
        </div>
      </header>

      {error && (
        <div className="border-b border-destructive-soft bg-destructive-soft px-6 py-3 text-sm text-destructive-soft-foreground">
          {error}
        </div>
      )}

      <div className="divide-y divide-border">
        {fields.map((f) => {
          if (evalHideWhen(f.hide_when, draft, payload)) return null;
          const v = readValue(f);
          const fv = payload.fields[f.key];
          const placeholder =
            fv && "placeholder" in fv && fv.placeholder ? fv.placeholder : undefined;
          const common = (
            <>
              {f.type === "string" && f.secret
                ? (() => {
                    const sfv = fv as
                      | { preview: string; has_value: boolean; env_set?: boolean }
                      | undefined;
                    return (
                      <SecretField
                        field={f}
                        preview={sfv?.preview ?? ""}
                        hasValue={sfv?.has_value ?? false}
                        envSet={sfv?.env_set ?? false}
                        onChange={(nv) => setField(f, nv)}
                        onClear={() => unsetField(f)}
                      />
                    );
                  })()
                : f.type === "string"
                ? (
                    <StringField
                      field={f}
                      value={String(v ?? "")}
                      placeholder={placeholder}
                      onChange={(nv) => setField(f, nv)}
                    />
                  )
                : f.type === "int"
                ? (() => {
                    // v can be "" (db empty) or a number/string from db / draft.
                    // Pass null when there's no actual value so IntField shows
                    // the placeholder instead of "0".
                    let intValue: number | null = null;
                    if (typeof v === "number" && Number.isFinite(v)) {
                      intValue = v;
                    } else if (typeof v === "string" && v !== "") {
                      const parsed = Number(v);
                      if (Number.isFinite(parsed)) intValue = parsed;
                    }
                    return (
                      <IntField
                        field={f}
                        value={intValue}
                        placeholder={placeholder}
                        onChange={(nv) => setField(f, nv)}
                      />
                    );
                  })()
                : f.type === "bool"
                ? (
                    <BoolField
                      field={f}
                      value={coerceBoolFieldValue(v)}
                      onChange={(nv) => setField(f, nv)}
                    />
                  )
                : null}
            </>
          );
          return (
            <div key={f.key} className="px-6 py-4">
              {common}
            </div>
          );
        })}
      </div>

      <footer className="flex flex-col gap-2 border-t border-border bg-muted/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-6">
        <div className="truncate text-xs text-muted-foreground">
          {dirty ? "변경사항이 저장되지 않았습니다" : "변경사항 없음"}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {group.actions?.includes("test") && (
            <button
              type="button"
              className="whitespace-nowrap rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow-sm hover:bg-muted disabled:opacity-50"
              onClick={onTest}
              disabled={saving}
            >
              Test connection
            </button>
          )}
          <button
            type="button"
            className="whitespace-nowrap rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow-sm hover:bg-muted disabled:opacity-50"
            onClick={reset}
            disabled={!dirty || saving}
          >
            Reset
          </button>
          <button
            type="button"
            className="whitespace-nowrap rounded-md bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50 disabled:hover:bg-primary"
            disabled={!dirty || saving}
            onClick={async () => {
              try {
                await onSave(buildPayload());
                setDraft({});
              } catch {
                // keep draft on error so user doesn't lose unsaved input
              }
            }}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </footer>
    </section>
  );
}
